import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import { IconPlus } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { useOptionalUrlTiles } from '../../../url-tiles';
import { useJobItemDetails } from '../../api/use-job-item-details';
import { useJobArchitecturePermissions } from '../../context/JobArchitecturePermissionsContext';
import { useTrackProfileTaskInteraction } from '../analytics/track-profile-task-interaction';
import {
    mapJobProfileTaskSummaries,
    orderSummariesByStructuredTaskElements,
} from '../api/profile-task-transport-adapter';
import {
    usePendingProfileTaskTitles,
    useProfileTaskDraftCoordinator,
    useProfileTaskParentDraft,
} from '../draft/ProfileTaskDraftCoordinatorProvider';
import type { ProfileTaskParentContext, ProfileTaskSummary } from '../model/profile-task';
import { resolveProfileTaskPermissions } from '../profile-task-permissions';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from '../tile/profile-task-tile-types';
import { createDraftProfileTaskTilePayload, createPersistedProfileTaskTilePayload } from '../tile/task-tile-url';
import { ProfileTaskSortableList } from './ProfileTaskSortableList';
import { type ProfileTaskSummariesInput, useProfileTasksAvailability } from './use-profile-tasks-availability';

const ProfileTasksSectionSkeleton: FunctionComponent = () => (
    <section className="flex flex-col gap-m" data-testid="profile-tasks-section-loading">
        <SkeletonBase className="h-5 w-24" />
        <SkeletonBase className="h-8 w-full" />
        <SkeletonBase className="h-8 w-4/5" />
    </section>
);

export const ProfileTasksSection: FunctionComponent<{
    readonly activeLanguage: LanguageCodeForJSA;
    readonly context: ProfileTaskParentContext;
    /** Task elements synthesized into the structured content: ref order and per-language titles. */
    readonly inlineTasks?: readonly { readonly taskId?: string | null; readonly value: string }[];
    readonly isEditing: boolean;
    readonly isLoading?: boolean;
    readonly parentCanEdit: boolean;
    readonly value: ProfileTaskSummariesInput;
}> = ({ activeLanguage, context, inlineTasks, isEditing, isLoading = false, parentCanEdit, value }) => {
    const availability = useProfileTasksAvailability(value);
    const { canRead: parentCanRead } = useJobArchitecturePermissions();
    const translate = useTranslate();
    const urlTiles = useOptionalUrlTiles();
    const coordinator = useProfileTaskDraftCoordinator();
    const parentDraft = useProfileTaskParentDraft(context);
    const pendingTaskTitles = usePendingProfileTaskTitles();
    const trackInteraction = useTrackProfileTaskInteraction(context);
    const availableSummaries = availability.status === 'available' ? availability.summaries : undefined;
    const summaries = useMemo(
        () => orderSummariesByStructuredTaskElements(availableSummaries ?? [], inlineTasks ?? []),
        [availableSummaries, inlineTasks],
    );
    const [moveAnnouncement, setMoveAnnouncement] = useState('');

    // Tasks generated during the edit session live on a newer item version than the one this
    // section is pinned to, so their rows resolve against the item's latest version instead.
    const hasTasksBeyondPinnedVersion =
        isEditing &&
        parentDraft !== undefined &&
        parentDraft.applied.some(
            (reference) =>
                reference.draft === undefined && !summaries.some(({ taskId }) => taskId === reference.taskId),
        );
    const latestDetails = useJobItemDetails({
        architectureId: context.architectureId,
        enabled: hasTasksBeyondPinnedVersion,
        itemId: context.parentId,
        itemType: context.parentType === 'role' ? 'job-role' : 'job-level',
        version: undefined,
    });
    const latestSummaries = useMemo(
        () => mapJobProfileTaskSummaries(latestDetails.data?.jobProfile?.tasks ?? []),
        [latestDetails.data],
    );
    const taskPermissions = resolveProfileTaskPermissions({
        hasStagedChanges: false,
        isParentEditing: isEditing,
        isPersisted: true,
        parentCanEdit,
        parentCanRead,
    });
    const isAuthoring = taskPermissions.canEdit;

    useEffect(() => {
        coordinator.getState().setParentLanguage(context, activeLanguage);
    }, [activeLanguage, context, coordinator]);

    useEffect(() => {
        if (!isAuthoring || availability.status !== 'available') {
            coordinator.getState().clearParent(context);
            return;
        }
        coordinator.getState().initialize(
            context,
            summaries.map(({ taskId, taskVersionId }) => ({ sourceTaskVersionId: taskVersionId, taskId })),
        );
    }, [availability.status, context, coordinator, isAuthoring, summaries]);

    const displayedSummaries = useMemo<readonly ProfileTaskSummary[]>(() => {
        if (!isAuthoring || parentDraft === undefined) {
            return summaries;
        }
        return parentDraft.applied.map((reference) => {
            const persisted = summaries.find(({ taskId }) => taskId === reference.taskId);
            if (reference.draft === undefined) {
                return (
                    persisted ??
                    latestSummaries.find(({ taskId }) => taskId === reference.taskId) ?? {
                        generationState: 'queued',
                        taskId: reference.taskId,
                        taskVersionId: reference.sourceTaskVersionId,
                        title: pendingTaskTitles.get(reference.taskId) ?? '',
                    }
                );
            }
            return {
                generationState: persisted?.generationState ?? 'finished',
                taskId: reference.taskId,
                taskVersionId: reference.sourceTaskVersionId ?? '',
                title: reference.draft.title,
            };
        });
    }, [isAuthoring, latestSummaries, parentDraft, pendingTaskTitles, summaries]);

    const reorderTask = (fromIndex: number, toIndex: number): void => {
        if (fromIndex === toIndex) {
            return;
        }
        const movedTask = displayedSummaries[fromIndex];
        if (movedTask === undefined) {
            return;
        }
        coordinator.getState().reorder(context, fromIndex, toIndex);
        setMoveAnnouncement(`${movedTask.title} moved to position ${toIndex + 1}`);
        trackInteraction({
            action: 'tasks-reordered',
            taskCount: displayedSummaries.length,
        });
    };

    // Creates the draft editor and opens the title-first create tile; the tile's "Add task"
    // is what fires the generation request.
    const addTask = (): void => {
        const taskId = globalThis.crypto.randomUUID();
        coordinator.getState().addTask({
            context,
            form: {
                language: activeLanguage,
                requiredSkillIds: [],
                title: '',
                workflowSteps: [],
            },
            taskId,
        });
        urlTiles?.openTile(createDraftProfileTaskTilePayload({ ...context, language: activeLanguage, taskId }));
    };

    useEffect(() => {
        if (availability.status !== 'available' || summaries.length === 0) {
            return;
        }

        trackInteraction({ action: 'section-exposed', taskCount: summaries.length });
    }, [availability.status, summaries.length, trackInteraction]);

    if (!taskPermissions.canRead || availability.status === 'disabled' || availability.status === 'unavailable') {
        return null;
    }

    if (isLoading) {
        return <ProfileTasksSectionSkeleton />;
    }

    if (displayedSummaries.length === 0 && !isAuthoring) {
        return null;
    }

    return (
        <section aria-label={translate(profileTaskTranslationKeys.title)} data-testid="profile-tasks-section">
            <div className="mb-xl flex items-center gap-l">
                <h3 className="m-0 text-body-s font-medium leading-normal text-text-primary">
                    {translate(profileTaskTranslationKeys.title)}
                </h3>
                {isAuthoring ? (
                    <Button className="ml-auto" size="sm" variant="secondary" onClick={addTask}>
                        <IconPlus />
                        {translate(profileTaskTranslationKeys.addTask)}
                    </Button>
                ) : null}
            </div>
            {displayedSummaries.length === 0 ? (
                <p className="mb-m mt-0 text-sm text-text-secondary">{translate(profileTaskTranslationKeys.empty)}</p>
            ) : (
                <ProfileTaskSortableList
                    isEditing={isAuthoring}
                    isSelected={(summary) =>
                        urlTiles?.tiles.some(
                            ({ payload }) =>
                                payload.type === JSA_PROFILE_TASK_DETAIL_TILE_TYPE &&
                                payload['taskId'] === summary.taskId,
                        ) ?? false
                    }
                    summaries={displayedSummaries}
                    onMove={reorderTask}
                    onOpen={(summary, index) => {
                        const sourceTaskVersionId =
                            parentDraft?.applied[index]?.sourceTaskVersionId ?? summary.taskVersionId;
                        urlTiles?.openTile(
                            sourceTaskVersionId === undefined || sourceTaskVersionId.length === 0
                                ? createDraftProfileTaskTilePayload({
                                      ...context,
                                      language: activeLanguage,
                                      taskId: summary.taskId,
                                  })
                                : createPersistedProfileTaskTilePayload({
                                      ...context,
                                      language: activeLanguage,
                                      taskId: summary.taskId,
                                      taskVersionId: sourceTaskVersionId,
                                  }),
                        );
                        trackInteraction({
                            action: 'task-opened',
                            taskId: summary.taskId,
                            taskVersionId: summary.taskVersionId,
                        });
                    }}
                    onRemove={(summary) => {
                        coordinator.getState().removeTask(context, summary.taskId);
                        urlTiles?.closeTile(`jsa.profile-task.${summary.taskId}`);
                        trackInteraction({
                            action: 'task-removed',
                            taskId: summary.taskId,
                        });
                    }}
                />
            )}
            <span className="sr-only" aria-live="polite">
                {moveAnnouncement}
            </span>
        </section>
    );
};
